import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { BookingRecordDetailsCustomer, OrderPaymentStatus, OrderRecordCustomer } from '@navio/nsdk';
import { CustomerGroupType } from '@navio/nsdk';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiBadge, NoddiBasicCard, PrettySlug } from 'noddi-ui-web';
import {
  CustomDateFormats,
  formatCurrencyAmount,
  formatLicensePlate,
  groupDiscountsByOrderLine,
  NoddiDateFormats,
  noddiFormatDate,
  stringToUpperCase
} from 'noddi-util';
import { DiscountLines, OrderLinePrice } from 'noddi-web';

type Props = {
  order: OrderRecordCustomer;
  booking?: BookingRecordDetailsCustomer;
};

const getOrderPaymentStatusLabel = (value: OrderPaymentStatus): string => {
  const labels: Record<OrderPaymentStatus, string> = {
    cancelled: t`Cancelled`,
    unpaid: t`Unpaid`,
    paid: t`Paid`,
    paid_external: t`Paid`,
    partially_paid: t`Partially paid`,
    overpaid: t`Overpaid`,
    refunded: t`Refunded`,
    partially_refunded: t`Partially refunded`,
    disputed: t`Disputed`
  };
  return labels[value] ?? value;
};

export const PointOfSalePaymentOrderDetailsCard = ({ order, booking }: Props) => {
  const discountsByLine = groupDiscountsByOrderLine(order.discounts);
  const isOrganization = order.customer.customerType === CustomerGroupType.ORGANIZATION;

  const bookingDate = booking
    ? stringToUpperCase(noddiFormatDate(booking.date, CustomDateFormats.FULL_DAY_MONTH))
    : null;
  const bookingTime = booking
    ? `${noddiFormatDate(booking.deliveryWindowStartsAt, NoddiDateFormats.TIME)} - ${noddiFormatDate(booking.deliveryWindowEndsAt, NoddiDateFormats.TIME)}`
    : null;

  return (
    <NoddiBasicCard className='flex flex-col gap-4'>
      <div className='flex items-center justify-between'>
        <h2 className='text-lg font-semibold text-primary'>
          <Trans>Order details</Trans>
        </h2>
        <NoddiBadge>{getOrderPaymentStatusLabel(order.paymentStatus.value)}</NoddiBadge>
      </div>

      <div className='flex flex-col gap-2 text-sm'>
        {isOrganization && order.customer.organizationName && (
          <div className='flex items-center gap-2'>
            <NoddiIcon name='Building2' size='small' className='text-muted-foreground' />
            <span className='font-medium'>{order.customer.organizationName}</span>
          </div>
        )}
        <div className='flex justify-between'>
          <span className='text-muted-foreground'>
            <Trans>Customer</Trans>
          </span>
          <span>{order.customer.name}</span>
        </div>
        {order.cars.map((car) => (
          <div key={car.licensePlate.number} className='flex flex-col gap-1'>
            <div className='flex justify-between'>
              <span className='text-muted-foreground'>
                <Trans>License plate</Trans>
              </span>
              <span className='font-medium'>{formatLicensePlate(car.licensePlate.number)}</span>
            </div>
            {(car.make || car.model) && (
              <div className='flex justify-between'>
                <span className='text-muted-foreground'>
                  <Trans>Car</Trans>
                </span>
                <span>
                  {[car.make, car.model].filter(Boolean).join(' ')}
                  {car.year ? ` (${car.year})` : ''}
                </span>
              </div>
            )}
          </div>
        ))}
        {bookingDate && (
          <div className='flex justify-between'>
            <span className='text-muted-foreground'>
              <Trans>Date</Trans>
            </span>
            <span>{bookingDate}</span>
          </div>
        )}
        {bookingTime && (
          <div className='flex justify-between'>
            <span className='text-muted-foreground'>
              <Trans>Time</Trans>
            </span>
            <span>{bookingTime}</span>
          </div>
        )}
        <div className='flex justify-between'>
          <span className='text-muted-foreground'>
            <Trans>Order</Trans>
          </span>
          <span>
            <PrettySlug slug={order.slug} />
          </span>
        </div>
      </div>

      {order.cars.some((car) => car.drivers?.length) && (
        <div className='flex flex-col gap-2 border-t pt-4'>
          <h3 className='text-sm font-medium'>
            <Trans>Drivers</Trans>
          </h3>
          {order.cars.flatMap((car) =>
            (car.drivers ?? []).map((driver) => (
              <div key={`${car.licensePlate.number}-${driver.name}`} className='flex flex-col gap-0.5 text-sm'>
                <span className='font-medium'>{driver.name}</span>
                <div className='flex flex-wrap gap-4 text-muted-foreground'>
                  {driver.phoneNumber && <span>{driver.phoneNumber}</span>}
                  {driver.email && <span>{driver.email}</span>}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <div className='flex flex-col gap-1 border-t pt-4'>
        {order.orderLines.map((line) => {
          const discounts = discountsByLine.get(line.id);
          return (
            <div key={line.id} className='flex flex-col gap-0.5'>
              <div className='flex items-center justify-between gap-4 text-sm'>
                <span className={line.isUnableToComplete ? 'text-muted-foreground line-through' : ''}>
                  {line.description}
                  {line.quantity > 1 && <span className='text-muted-foreground'> × {line.quantity}</span>}
                </span>
                <div className='flex shrink-0 items-center gap-2'>
                  {line.quantity > 1 && (
                    <span className='text-xs text-muted-foreground'>
                      {formatCurrencyAmount({ money: line.amountGrossPerUnit })} × {line.quantity}
                    </span>
                  )}
                  <OrderLinePrice
                    price={line.amountGross}
                    lineTotalGross={line.lineTotalGross}
                    hasDiscounts={Boolean(discounts)}
                  />
                </div>
              </div>
              {discounts && <DiscountLines discounts={discounts} />}
            </div>
          );
        })}
      </div>

      <div className='flex flex-col gap-1 border-t pt-4 text-sm'>
        <div className='flex justify-between'>
          <span className='text-muted-foreground'>
            <Trans>Net</Trans>
          </span>
          <span>{formatCurrencyAmount({ money: order.amountNet })}</span>
        </div>
        <div className='flex justify-between'>
          <span className='text-muted-foreground'>
            <Trans>VAT</Trans>
          </span>
          <span>{formatCurrencyAmount({ money: order.amountVat })}</span>
        </div>
        <div className='flex justify-between text-base font-semibold'>
          <span>
            <Trans>Total</Trans>
          </span>
          <span>{formatCurrencyAmount({ money: order.amountGross })}</span>
        </div>
      </div>
    </NoddiBasicCard>
  );
};
